import { Plus, X } from "lucide-react";
import { type RefObject, useState } from "react";
import { DraftFields, TextField } from "../../web/components/fields";
import { DraftBar } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { usePriceListSaver } from "./data";
import type { GroupRow, MarkupTier, PriceListData } from "./meta";
import { PricingCard } from "./PricingTab";

/*
 * A markup group's pricing, on its window's Pricing tab (as a fixed price's is there: GroupWindow.tsx), laid out as ServiceM8's:
 * one markup on every cost, or tiered by cost (each tier from its minimum cost, before tax, to the next one's: more on cheap parts,
 * less on dear ones). Which of them is chosen above it (its window's Kind). A draft: Save or Cancel once it's changed; saving shows
 * its items' new prices to update (groups.ts). A fixed price switched to a markup is a draft of one until it's saved.
 */

type Kind = "markup" | "tiered";
/** A markup group's tiers being changed, not saved yet: kept by its window, so closing it (or leaving the page) asks first. */
export type TiersDraft = { kind: Kind; tiers: MarkupTier[] };
type Draft = TiersDraft;

/** Its tiers as kept, as a draft would have them; none (a fixed price): null. */
export function savedTiers(group: GroupRow): TiersDraft | null {
    if (!group.tiers) return null;
    return { kind: group.tiers.length > 1 ? "tiered" : "markup", tiers: group.tiers };
}

/** Switching to `kind` (from what's drafted, or kept): tiered with one tier yet gets a second to fill in; a fixed price starts at 0%. Null: back to as kept. */
export function tiersDraftOf(group: GroupRow, from: TiersDraft | null, kind: Kind): TiersDraft | null {
    const start = from ?? savedTiers(group) ?? { kind, tiers: [{ minCost: "0", markup: "" }] };
    const next: TiersDraft = { kind, tiers: kind === "tiered" && start.tiers.length < 2 ? [...start.tiers, { minCost: "", markup: "" }] : start.tiers };
    const saved = savedTiers(group);
    return saved && same(next, saved) ? null : next;
}

/** What's wrong with it as drafted (said where Save is), or null. */
function problemOf(d: Draft): string | null {
    const tiers = d.kind === "markup" ? d.tiers.slice(0, 1) : d.tiers;
    if (tiers.some((t) => t.markup.trim() === "" || !Number.isFinite(Number(t.markup)) || Number(t.markup) < 0)) return "Each markup is a percentage, 0 or more.";
    if (d.kind === "tiered") {
        const mins = tiers.slice(1).map((t) => t.minCost.trim());
        if (mins.some((m) => m === "" || !Number.isFinite(Number(m)) || Number(m) <= 0)) return "Each tier after the first starts at a cost above $0.";
        if (new Set(mins.map(Number)).size !== mins.length) return "Two tiers start at the same cost.";
    }
    return null;
}

const same = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);

/**
 * A markup group's tiers (its kind: one markup, or tiered), edited as a draft (`draft`, kept by the window). `boxRef`: brought
 * into view when the window asks about it.
 */
export function TiersCard(
    props: Readonly<{
        data: PriceListData;
        group: GroupRow;
        canEdit: boolean;
        draft: TiersDraft | null;
        onDraft: (draft: TiersDraft | null) => void;
        boxRef?: RefObject<HTMLDivElement | null>;
    }>,
) {
    const { group } = props;
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const saved = savedTiers(group);
    const { draft, onDraft: setDraft } = props;
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const d = draft ?? saved ?? { kind: "markup", tiers: [{ minCost: "0", markup: "" }] };
    const edit = (patch: Partial<Draft>) => {
        const next = { ...d, ...patch };
        setDraft(saved && same(next, saved) ? null : next);
        return Promise.resolve();
    };
    const setTier = (i: number, patch: Partial<MarkupTier>) => edit({ tiers: d.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
    const tiers = d.kind === "markup" ? d.tiers.slice(0, 1) : d.tiers;
    const problem = draft ? problemOf(d) : null;
    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            await saver.updateGroup(group.id, { tiers: tiers.map((t, i) => ({ minCost: i === 0 ? "0" : t.minCost.trim(), markup: t.markup.trim() })) });
            setDraft(null);
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    };
    const disabled = !props.canEdit || busy;
    const tiered = d.kind === "tiered";
    return (
        <PricingCard aside="On each product's own cost, before tax. Each keeps its own tax.">
            <DraftFields>
                <div ref={props.boxRef} className={draft ? "-mx-4 -mt-3.5 -mb-4 space-y-3 rounded-md bg-brand-500/5 px-4 pt-3.5 pb-4" : "space-y-3"}>
                    <div className="max-w-xl">
                        {tiered ? (
                            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem] gap-3 border-b border-line pb-1.5 text-xs text-muted">
                                <span>Costs from</span>
                                <span>Markup</span>
                                <span />
                            </div>
                        ) : null}
                        {tiers.map((t, i) => (
                            // By position: a tier's minimum can be typed into the order the server keeps (sorted by cost).
                            <div key={i} className={tiered ? "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem] items-center gap-3 border-b border-line py-1.5 last:border-b-0" : "max-w-xs"}>
                                {tiered ? (
                                    i === 0 ? (
                                        // The first from $0, always: a box like the others (so the rows line up), not to change.
                                        <TextField label="Tier 1 minimum cost" hideLabel value="0" prefix={fmt.symbol} disabled onSave={async () => undefined} />
                                    ) : (
                                        <TextField label={`Tier ${i + 1} minimum cost`} hideLabel value={t.minCost} prefix={fmt.symbol} inputMode="decimal" disabled={disabled} live onSave={(minCost) => setTier(i, { minCost })} />
                                    )
                                ) : null}
                                <TextField
                                    label={tiered ? `Tier ${i + 1} markup` : "Markup on every cost"}
                                    hideLabel={tiered}
                                    value={t.markup}
                                    suffix="%"
                                    inputMode="decimal"
                                    disabled={disabled}
                                    live
                                    onSave={(markup) => setTier(i, { markup })}
                                />
                                {tiered ? (
                                    i === 0 || disabled ? (
                                        <span />
                                    ) : (
                                        <button type="button" className="btn-icon" aria-label={`Remove tier ${i + 1}`} title="Remove this tier" onClick={() => void edit({ tiers: d.tiers.filter((_, j) => j !== i) })}>
                                            <X className="size-4" />
                                        </button>
                                    )
                                ) : null}
                            </div>
                        ))}
                        {tiered && !disabled ? (
                            <button type="button" className="btn-secondary mt-2 px-2.5 py-1 text-xs" onClick={() => void edit({ tiers: [...d.tiers, { minCost: "", markup: "" }] })}>
                                <Plus className="size-3.5" /> Add tier
                            </button>
                        ) : null}
                    </div>
                    {draft ? (
                        <DraftBar
                            message={problem ? <span className="text-danger">{problem}</span> : "Saving changes its products' prices at once; items linked to them then show theirs to update."}
                            busy={busy}
                            error={error}
                            actions={
                                <>
                                    <button type="button" className="btn-primary" disabled={busy || problem !== null} onClick={() => void save()}>
                                        Save
                                    </button>
                                    <button type="button" className="btn-secondary" disabled={busy} onClick={() => setDraft(null)}>
                                        Cancel
                                    </button>
                                </>
                            }
                        />
                    ) : null}
                </div>
            </DraftFields>
        </PricingCard>
    );
}
